<template>
    <div class="flex flex-col w-full">
<!--        <span class="text-xs text-gray-700">-->
<!--            {{ field.translated?.label }}-->
<!--            <span v-if="isRequired" class="text-[rgba(10,69,143,1)]">*</span>-->
<!--        </span>-->

        <div class="relative w-full">
            <button
                type="button"
                @click="toggleDropdown"
                class="w-full flex items-center justify-between pl-3 pr-3 py-4 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-sm font-bold text-gray-700 transition-all"
                :class="hasError ? 'ring-1 ring-red-500' : 'border-gray-300'"
            >
                <div class="flex items-center gap-2">
                    <img
                        v-if="selectedOptionFlag"
                        :src="selectedOptionFlag"
                        :alt="selectedOptionLabel"
                        class="w-5 h-5 rounded-full object-cover flex-shrink-0"
                    />
                    <span class="text-left">
                        {{ selectedOptionLabel }}
                    </span>
                </div>

                <img
                    v-if="resolvedAngleDownSolid"
                    :src="resolvedAngleDownSolid"
                    :alt="angleDownSolidAlt"
                    class="w-4 h-4"
                />
            </button>


            <div
                v-if="isDropdownOpen"
                class="absolute z-20 mt-1 w-full bg-white border rounded-[12px] shadow max-h-60 overflow-auto"
            >
                <div class="p-2">
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Search..."
                        class="w-full px-3 py-2 border rounded text-sm"
                    />
                </div>

                <div
                    v-for="(option, index) in filteredOptions"
                    :key="option.code"
                    class="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center"
                    @click="selectOption(option)"
                    :class="{'bg-gray-200': option.code === currentValue}"
                >
                    <img
                        :src="option.flag || ''"
                        :alt="option.translated?.name || option.code || ''"
                        class="w-5 h-5 mr-2 rounded-full object-cover flex-shrink-0"
                    />
                    <span>{{ option.translated?.name }}</span>
                </div>
            </div>
        </div>
        <!--            <p v-if="hasError" class="text-xs text-red-500 mt-1">This field is required</p>-->
    </div>
</template>

<script>
import { resolveDefaultVehicleCountryCode } from '../../../utils/languageDefaultCountry.js';

export default {
    name: "DropdownField",
    emits: ['clear-error', 'field-change'],
    props: {
        field: {
            type: Object,
            required: true
        },
        stepData: {
            type: Object,
            required: true
        },
        hasError: { type: Boolean, default: false },
        angleDownSolid: {
            type: [String, Object],
            required: false,
            default: null
        }
    },
    data() {
        return {
            isDropdownOpen: false,
            searchQuery: ''
        };
    },
    computed: {
        resolvedAngleDownSolid() {
            if (!this.angleDownSolid) return null;
            if (typeof this.angleDownSolid === 'string') return this.angleDownSolid;
            return this.angleDownSolid.src ?? this.angleDownSolid.url ?? null;
        },
        angleDownSolidAlt() {
            if (!this.angleDownSolid || typeof this.angleDownSolid !== 'object') return '';
            return this.angleDownSolid.translated?.alt ?? '';
        },
        isRequired() {
            return this.field.validation_rules?.includes("required");
        },
        /** Flat field key or nested e.g. invoice_vat + nestedKey "country" */
        currentValue() {
            if (this.field.nestedKey) {
                const parent = this.stepData[this.field.key];
                if (!parent || typeof parent !== 'object') return '';
                return parent[this.field.nestedKey] || '';
            }
            return this.stepData[this.field.key] || '';
        },
        selectedOptionLabel() {
            const value = this.currentValue;
            if (!value) {
                return this.$store?.getters?.texts?.['select-option'] ?? 'Select option';
            }

            const option = this.field.options.find(o => o.code === value);
            return option ? option.translated?.name : "Unknown";
        },
        selectedOptionFlag() {
            const value = this.currentValue;
            if (!value) {
                return '';
            }

            const option = this.field.options.find(o => o.code === value);
            return option ? option.flag : '';
        },
        filteredOptions() {
            if (!this.searchQuery) return this.field.options;

            const query = this.searchQuery.toLowerCase();
            return this.field.options.filter(option =>
                (option.translated?.name || '').toLowerCase().includes(query) ||
                (option.code || '').toLowerCase().includes(query)
            );
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);

        if (this.field.preventAutoSelect || this.field.nestedKey) {
            return;
        }
        /**
         * plate_country is owned by Vuex action `form/syncVehicleCountryToLanguage`
         * (autosync + manual-override mirror). Filling it from here would race the
         * Vuex sync — DropdownField.mounted runs in $nextTick BEFORE loadInitialData
         * resolves, so the pre-fill would land before the mirror is written and the
         * very next syncVehicleCountryToLanguage call would treat it as a manual
         * override (mirror empty + current set) and disable autosync for good.
         */
        if (this.field.key === 'plate_country') {
            return;
        }
        if (!this.stepData[this.field.key] && this.field.options.length > 0) {
            this.$nextTick(() => {
                let code;
                if (this.field.key === 'invoice_country' && this.$store) {
                    const lang = this.$store.getters.lang;
                    code = resolveDefaultVehicleCountryCode(lang?.code, this.field.options);
                } else {
                    code = this.field.options[0]?.code;
                }
                if (code) this.stepData[this.field.key] = code;
            });
        }
    },
    // beforeDestroy() {
    //     document.addEventListener('click', this.handleOutsideClick);
    // },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
    methods: {
        handleOutsideClick(event) {
            if (!this.$el.contains(event.target)) {
                this.isDropdownOpen = false;
            }
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
            if (this.isDropdownOpen) {
                this.searchQuery = '';
            }
        },
        selectOption(option) {
            const value = option.code;
            if (this.field.nestedKey) {
                if (!this.stepData[this.field.key] || typeof this.stepData[this.field.key] !== 'object') {
                    this.stepData[this.field.key] = { country: '', number: '' };
                }
                this.stepData[this.field.key][this.field.nestedKey] = value;
            } else {
                this.stepData[this.field.key] = value;
            }
            this.isDropdownOpen = false;
            const errKey = this.field.nestedKey
                ? `${this.field.key}.${this.field.nestedKey}`
                : this.field.key;
            this.$emit('clear-error', errKey);
            /**
             * `manual: true` is intentionally emitted ONLY from this user-driven path.
             * ShoppingCart / EnterDetails listen and dispatch `form/markPlateCountryManual`
             * to disable language-driven autosync of plate_country going forward.
             */
            this.$emit('field-change', {
                key: this.field.key,
                nestedKey: this.field.nestedKey || null,
                value,
                manual: true
            });
        }
    }
};
</script>
